import * as React from "react";
import { StepsProps } from "@bizform/primereact/components/steps/Steps";
import { MenuItem } from "@bizform/primereact/components/menuitem/MenuItem";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import { DataType } from "@bizform/type/model/model/DataType.model";
import { DefaultLayoutId } from "@bizform/type/model/component_element/DefaultLayoutId.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty,
    ComponentElementPropertyType
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { ComponentHelper, Debug } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";
import { BrowserHelper } from "@bizform/helper/dist/js/bizflow/helper.min";

type Props = StepsProps & {
};

type WrappedCompState = {
    // attribute
    id?: string;
    index?: boolean;
    tabindex?: number;
    stepModel: any;
    activeIndex?: number;
    readOnly?: boolean;
    style?: object;
    className?: string;
    alignIndex?: boolean;
    minWidth?: number;
    labelStep?: boolean;

    // event
    onSelect?(e: {originalEvent: Event, item: MenuItem, index: number}): void;
};

type WrappedFormState = {
    onSelect?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Byungmin Choi, Chris Park
 * @class
 * @description
 * Step component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<number>>()
export class BfSteps extends AbstractComponentElement<Props, State> {

    // {{{ Implements ComponentElementCommon(ComponentElement) interface
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_CtfsriokLTSwQZDleDmJk43uRO5cTF_",
        category: ComponentCategory.MISC,
        label: I18n.componentElement.steps.label,
        description: I18n.componentElement.steps.description,
        defaultLayoutId: DefaultLayoutId.WITHOUTLABEL,
        dataType: [DataType.INTEGER],
        icon: "",
        property: null
    };

    focus(): void {
        this._steps.querySelector("a").focus();
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            if (property.attribute.model) {
                delete(property.attribute.model);
            }
            if (property.attribute.stepModel && property.attribute.stepModel.value && property.attribute.stepModel.value.items) {
                property.attribute.model = {};
                property.attribute.model.value = property.attribute.stepModel.value.items;
                property.attribute.model.type = ComponentElementPropertyType.FORMSTATE;
            }
            const newState = ComponentHelper.createStateWithProperties<State, null>(
                property,
                this);
            this.setState(newState);
        } catch (e) {
            Debug.console.error("setProperty", e);
        }
    }

    private _itemValueEvent(e: {originalEvent: Event, item: MenuItem}): void {
        /*
         * 주의:
         * itemValue 는 MenuItem 에 정의된 값이 프로퍼티가 아니다.
         * property panel 에서 form state 변경을 위해 itemValue 를 컴포넌트에 설정하고
         * command 함수(메뉴 클릭)가 호출될 때 이 itemValue 를 전달하기 위해
         * MenuItem 객체에 강제로 주입된 속성이다.
         */
        const itemValue = e.item && (e.item as {itemValue}).itemValue;
        if (itemValue) {
            this.emitFormState(itemValue);
        }
    }

    emit(value: number): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: number): void {
        this.setState({
            wrappedCompState: {
                ...this.state.wrappedCompState,
                activeIndex: value
            }
        });
    }

    @ReactComponentElementInit(BfSteps)
    static init(): ComponentElement<number> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    static defaultProps = {
    };

    private _steps: any;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                stepModel: props.model
            },
            wrappedFormState: {}
        };

        this._setRef = this._setRef.bind(this);
        this._changeIdx = this._changeIdx.bind(this);
    }

    private _onSelectEvent(e: any): void {
        const newState = {
            wrappedCompState : {
                ...this.state.wrappedCompState,
                activeIndex: Number(e.index + 1)
            }
        };
        this.setState(newState, this._onSelectEventCallback);
    }

    private _onSelectEventCallback() {
        this.emit(this.state.wrappedCompState.activeIndex);
    }

    componentDidUpdate(prevProps: Readonly<StepsProps & {}>, prevState: Readonly<BaseStateModel & { wrappedCompState: WrappedCompState; wrappedFormState: WrappedFormState }>, snapshot?: any): void {
        if (this._steps && this.state.wrappedCompState.tabindex !== this.___tabindex) {
            ComponentHelper.tabindex(this.displayMode, this._steps.querySelector("a"), this.state.wrappedCompState.tabindex);
            this.___tabindex = this.state.wrappedCompState.tabindex;
        }
    }

    componentWillUnmount(): void {
        this._steps = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Debug.console.error(error, errorInfo);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            /*
             * PanelMenu doesn't have readOnly, disabled properties.
             */
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4ReadOnly();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: Props): React.ReactNode {
        const {stepModel, style, className, activeIndex} = this.state.wrappedCompState;
        const stepValue = this._makeStep(stepModel, activeIndex);
        return (
            <div className={this.state.displayMode === 3 ? "p-disabled" : ""} style={{width: "100%"}} ref={this._setRef}>
                <div style={style} className={className}>
                    {stepValue}
                </div>
            </div>
        );
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            model: [],
            readOnly: false
        };
        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            model: [],
            readOnly: true
        };
        return this._render(newProps);
    }

    private _setRef(ref): void {
        if (ref) {
            this._steps = ref;
            this.readyComponent();
        }
    }

    private _makeStep(stepModel, actIdx) {
        const {labelStep, alignIndex, minWidth} = this.state.wrappedCompState;
        const defaultMinWidth = minWidth === undefined ? 100 : minWidth ? minWidth : 100;
        let data = labelStep === undefined ? this._makeNormalStep(stepModel, actIdx) : labelStep ? this._makeLabelStep(stepModel, actIdx) : this._makeNormalStep(stepModel, actIdx);
        const flexAlignWay = alignIndex === undefined ? (labelStep ? "center" : "space-around") : alignIndex ? "space-around" : "flex-start";
        const gridAlignWay = alignIndex === undefined ? "auto-fit" : alignIndex ? "auto-fit" : "auto-fill";

        return (
            <div style={{backgroundImage: "url('./plugin/builtin/component_element/primereact/img/step.png?v=1.2.6')", backgroundRepeat: "repeat", paddingTop: "10px", backgroundSize: "84px"}}>
                <div style={BrowserHelper.isIE11()
                ? {display: "flex", justifyContent: flexAlignWay, flexWrap: "wrap"}
                : {display: "grid", gridTemplateColumns: `repeat(${gridAlignWay}, minmax(${defaultMinWidth}px, 2fr))`, gridTemplateRows: "1fr"}}>
                    {data}
                </div>
            </div>
        );
    }

    private _makeNormalStep(stepModel, actIdx) {
        let data = [];
        const {minWidth, index, id} = this.state.wrappedCompState;
        const defaultMinWidth = minWidth === undefined ? 100 : minWidth ? minWidth : 100;
        let backColor, textColor, labelColor, labelWeight, idValue;
        for (let i = 0; i < stepModel.count; i++) {
            if ((Number(actIdx)) === i + 1) {
                backColor = "#0986b2", textColor = "white", labelColor = "#333333", labelWeight = 700;
                idValue = id;
            } else {
                backColor = "white", textColor = "black", labelColor = "#848484", labelWeight = 400;
            }
            data.push (
                <a style={{height: "90px", minWidth: `${defaultMinWidth}px`, maxWidth: `${window.innerWidth}px`, textDecoration: "none", paddingTop: "20px", outlineColor: "#8dcdff"}} href="#" id={id} tabIndex={0} key={`${stepModel.count}_${i}_`} onClick={(event) => this._changeIdx(event, stepModel.items[i], i)}>
                    <div style={{border: "1px solid #c8c8c8", borderRadius: "50%", color: textColor, width: "29px", margin: "0px auto", textAlign: "center", fontSize: "18px", backgroundColor: backColor}}>
                        {index ? i : i + 1}
                    </div>
                    <div title={stepModel.items[i].label} style={{color: labelColor, fontWeight: labelWeight, marginTop: "4px", fontFamily: "Open Sans, Helvetica Neue, sans-serif", textAlign: "center", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis"}}>
                        &nbsp;{stepModel.items[i].label}
                    </div>
                </a>
            );
        }
        return data;
    }

    private _makeLabelStep(stepModel, actIdx, ie?) {
        const {minWidth, id} = this.state.wrappedCompState;
        const defaultMinWidth = minWidth === undefined ? 100 : minWidth ? minWidth : 100;
        const topMargin = BrowserHelper.isIE11() ? "-1px" : "-3px";
        let data = [];
        let backColor, textColor, labelColor, labelWeight, idValue;
        for (let i = 0; i < stepModel.count; i++) {
            if ((Number(actIdx)) === i + 1) {
                backColor = "#0986b2", textColor = "white", labelColor = "#333333", labelWeight = 700;
                idValue = id;
            } else {
                backColor = "white", textColor = "black", labelColor = "#848484", labelWeight = 400;
            }
            data.push (
                <a style={{height: "90px", display: "flex", maxWidth: `${window.innerWidth}px`, minWidth: `${defaultMinWidth}px`, paddingTop: "17px", outlineColor: "#8dcdff", textDecoration: "none", zIndex: 1}} href="#" id={idValue} tabIndex={0} key={`${stepModel.count}_${i}_`} onClick={(event) => this._changeIdx(event, stepModel.items[i], i)}>
                    <div style={{border: "5px solid #0986B2", height: "34px", width: "100%", textAlign: "center", minWidth: `29px`, backgroundColor: backColor, borderRight: "none"}}>
                        <div title={stepModel.items[i].label} style={{color: textColor, fontWeight: labelWeight, fontSize: "12px", fontFamily: "Open Sans, Helvetica Neue, sans-serif", textAlign: "center", paddingTop: "3px", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis"}}>
                            {stepModel.items[i].label}&nbsp;
                        </div>
                    </div>
                    <div style={{height: "30px", fontSize: "40px", color: "#0986B2", marginLeft: "-6px", marginTop: topMargin, marginRight: "-7px", zIndex: 2}} className="fas fa-chevron-right"></div>
                    <div style={{display: "block", border: "12px solid transparent", marginTop: "5px", borderLeftColor: backColor, zIndex: 1, height: "25px", marginLeft: "-14px"}}></div>
                </a>
            );
        }
        return data;
    }

    private _changeIdx(event, item, index) {
        if (this.displayMode === ComponentElementDisplayMode.EDIT) {
            const {onSelect, readOnly} = this.state.wrappedCompState;
            if (readOnly || item.disabled) {
                event.preventDefault();
                return;
            }
            if (onSelect) {
                onSelect({
                    originalEvent: event,
                    item: item,
                    index: index
                });
            }
            if (!item.url) {
                event.preventDefault();
            }
            if (item.command) {
                item.command({
                    originalEvent: event,
                    item: item,
                    index: index
                });
            }
        }
    }
}
